import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from './ui/card';
import { Loader2, Link as LinkIcon, Copy, Check, Trash2, Eye, MousePointerClick, Send, X, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * ShareWithStakeholderPanel
 * -------------------------
 * Renders inside the Generate step of the Business Case Builder.
 * Lets the prospectus owner mint a tokenised public-share URL
 * (no auth required for the recipient) and tracks engagement
 * counters (views + CTA clicks). The first CTA click fires a
 * one-shot "hot prospect" Resend email + Slack/Teams ping to the
 * owner via the backend `prospectus-share` routes.
 */
export default function ShareWithStakeholderPanel({ prospectusId, prospectusSaved }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recipientLabel, setRecipientLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [emailDialogToken, setEmailDialogToken] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [sending, setSending] = useState(false);
  const [channelHealth, setChannelHealth] = useState(null);

  const refresh = useCallback(async () => {
    if (!prospectusId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/prospectus/${prospectusId}/share`);
      setShares(Array.isArray(data) ? data : []);
    } catch (e) {
      // 404 → prospectus not yet saved; harmless.
      if (e?.response?.status !== 404) {
        toast.error('Failed to load share links');
      }
    } finally {
      setLoading(false);
    }
  }, [prospectusId]);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!prospectusId) {
      toast.error('Save the prospectus first to enable share links.');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post(`/prospectus/${prospectusId}/share`, {
        recipient_label: recipientLabel.trim() || null,
        expires_in_days: 60,
      });
      const url = `${window.location.origin}${data.share_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard');
      } catch {
        toast.success('Share link created');
      }
      setRecipientLabel('');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    try {
      await api.delete(`/prospectus/share-tokens/${token}`);
      toast.success('Share link revoked');
      await refresh();
    } catch (e) {
      toast.error('Failed to revoke link');
    }
  };

  const copyOne = async (token) => {
    const url = `${window.location.origin}/prospectus/share/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success('Copied');
      setTimeout(() => setCopiedToken(null), 1800);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const openEmailDialog = (token) => {
    setEmailDialogToken(token);
    setEmailRecipient('');
    setEmailRecipientName('');
    setEmailNote('');
    // Preflight check — if the email channel is currently degrading we
    // surface an inline warning before the user clicks Send. Soft-fail:
    // if the endpoint 403s (non-admin) or errors we just hide the banner.
    (async () => {
      try {
        const { data } = await api.get('/admin/email-channel-health');
        setChannelHealth(data);
      } catch {
        setChannelHealth(null);
      }
    })();
  };

  const sendEmail = async () => {
    if (!emailRecipient.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(
        `/prospectus/share-tokens/${emailDialogToken}/email`,
        {
          recipient_email: emailRecipient.trim(),
          recipient_name: emailRecipientName.trim() || null,
          personal_note: emailNote.trim() || null,
          // Tell the backend if a deliverability warning was visible at
          // send-time so it can audit-log "user proceeded anyway".
          acknowledged_deliverability_warning: channelHealth?.status === 'degrading',
        }
      );
      if (data?.suppressed) {
        toast.error(`This recipient is on the suppression list (${data.reason}). Email not sent.`);
      } else {
        toast.success(`Sent — ${emailRecipient}`);
        setEmailDialogToken(null);
        await refresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mt-8 border-rose-200 bg-rose-50/40" data-testid="share-with-stakeholder-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-rose-600" />
          <CardTitle className="text-lg">Share with a stakeholder</CardTitle>
        </div>
        <CardDescription>
          Create a tokenised public link that a CFO or sponsor can open without
          logging in. We'll alert you the moment they click the CTA — that's
          the strongest engagement signal we track.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label htmlFor="recipient-label" className="text-xs">Who is this for?</Label>
            <Input
              id="recipient-label"
              value={recipientLabel}
              onChange={(e) => setRecipientLabel(e.target.value)}
              placeholder="e.g. CFO @ Acme · Friday board prep"
              maxLength={120}
              data-testid="share-recipient-label"
            />
          </div>
          <Button
            onClick={create}
            disabled={creating || !prospectusSaved}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="share-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LinkIcon className="w-4 h-4 mr-2" />}
            Create share link
          </Button>
        </div>

        {!prospectusSaved && (
          <p className="text-xs text-rose-700 bg-rose-100 rounded p-2">
            Save the prospectus first to enable share links.
          </p>
        )}

        {loading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : shares.length === 0 ? (
          <p className="text-xs text-slate-500">No share links yet.</p>
        ) : (
          <ul className="divide-y divide-rose-200 rounded border border-rose-200 bg-white" data-testid="share-list">
            {shares.map((s) => (
              <li key={s.token} className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {s.recipient_label || 'Untitled share'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {window.location.origin}/prospectus/share/{s.token.slice(0, 8)}…
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600">
                  <span title="Views" className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" />{s.view_count || 0}
                  </span>
                  <span title="CTA clicks" className="flex items-center gap-1 font-semibold text-rose-700">
                    <MousePointerClick className="w-3.5 h-3.5" />{s.cta_click_count || 0}
                  </span>
                  {s.hot_prospect_email_sent_at && (
                    <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold tracking-wide">
                      🔥 NUDGED
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyOne(s.token)}
                    data-testid={`share-copy-${s.token.slice(0, 6)}`}
                  >
                    {copiedToken === s.token ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    onClick={() => openEmailDialog(s.token)}
                    data-testid={`share-send-email-${s.token.slice(0, 6)}`}
                    title={s.emailed_at ? `Last sent ${new Date(s.emailed_at).toLocaleString()}` : 'Send to CFO via email'}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    {s.emailed_at ? 'Re-send' : 'Send'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-700 hover:bg-rose-50"
                    onClick={() => revoke(s.token)}
                    data-testid={`share-revoke-${s.token.slice(0, 6)}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {emailDialogToken && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
          data-testid="share-email-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setEmailDialogToken(null); }}
        >
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl border border-slate-200">
            <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Send share link via email</h3>
                <p className="text-xs text-slate-500 mt-1">
                  We'll send your recipient a clean, branded email with the link inline.
                  Suppression list is honoured automatically.
                </p>
              </div>
              <button
                onClick={() => setEmailDialogToken(null)}
                className="text-slate-400 hover:text-slate-700 -mr-1"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {channelHealth?.status === 'degrading' && (
                <div
                  className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2.5 text-xs text-rose-900"
                  data-testid="share-email-deliverability-warning"
                  role="alert"
                >
                  <AlertOctagon className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold">Heads up — email deliverability is currently degrading.</p>
                    <p className="mt-0.5 text-rose-800/90">
                      Recent recipients have been bouncing or marking us as spam. This send may still succeed for clean addresses, but consider reviewing the bounce trail before sending to a high-stakes stakeholder.
                    </p>
                    <a
                      href="/admin/email-suppressions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-1 text-rose-700 underline hover:text-rose-900"
                      data-testid="share-email-deliverability-warning-link"
                    >
                      View bounce details →
                    </a>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="email-recipient" className="text-xs">Recipient email</Label>
                <Input
                  id="email-recipient"
                  type="email"
                  placeholder="cfo@acme.example"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  data-testid="share-email-recipient"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="email-recipient-name" className="text-xs">Recipient name (optional)</Label>
                <Input
                  id="email-recipient-name"
                  placeholder="Jane Doe"
                  value={emailRecipientName}
                  onChange={(e) => setEmailRecipientName(e.target.value)}
                  data-testid="share-email-recipient-name"
                />
              </div>
              <div>
                <Label htmlFor="email-note" className="text-xs">Personal note (optional)</Label>
                <textarea
                  id="email-note"
                  placeholder="Hi Jane — here's the case we discussed Friday. Happy to walk through the assumptions whenever works."
                  value={emailNote}
                  onChange={(e) => setEmailNote(e.target.value)}
                  rows={4}
                  maxLength={600}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  data-testid="share-email-note"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">{emailNote.length}/600 chars</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <Button variant="ghost" onClick={() => setEmailDialogToken(null)} data-testid="share-email-cancel">
                Cancel
              </Button>
              <Button
                onClick={sendEmail}
                disabled={sending || !emailRecipient.includes('@')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="share-email-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send email
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
